import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Building2, User, Mail, Lock, Phone } from 'lucide-react';
import inviteService from '../services/inviteService';
import { useBusinessContext } from '../contexts/BusinessContext';

// Industry icon mapping (same as useTheme)
const industryIcons = {
  'PressureWashing': '💦',
  'Landscaping': '🌳',
  'WindowWashing': '🪟',
  'TreeService': '🌲',
  'ChristmasLights': '🎄',
  'JunkRemoval': '🚚',
  'Other': '🏢'
};

// Color mapping for industries (same as useTheme)
const getPrimaryColor = (serviceType) => {
  const colors = {
    'PressureWashing': '#3B82F6', // Blue
    'Landscaping': '#10B981',     // Green
    'WindowWashing': '#06B6D4',   // Cyan
    'TreeService': '#059669',     // Emerald
    'ChristmasLights': '#DC2626', // Red
    'JunkRemoval': '#F59E0B',     // Amber
    'Other': '#6366F1'            // Indigo (default)
  };
  return colors[serviceType] || colors.Other;
};

const EmployeeRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useBusinessContext();
  
  const [token] = useState(searchParams.get('token'));
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [businessTheme, setBusinessTheme] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    inviteToken: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
  });

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setValidating(false);
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setValidating(true);
      const data = await inviteService.validateInviteToken(token);
      
      if (data.isValid) {
        setInviteData(data);
        setFormData(prev => ({
          ...prev,
          inviteToken: token,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
        }));
        
        // ✅ Set business theme from backend response
        setBusinessTheme({
          serviceType: data.primaryServiceType || 'Other',
          displayName: data.businessName || 'Business',
          primaryColor: getPrimaryColor(data.primaryServiceType)
        });
        
        setError(null);
      } else {
        setError(data.errorMessage || 'Invalid invitation');
      }
    } catch (err) {
      console.error('Error validating token:', err);
      setError('Failed to validate invitation. Please check the link and try again.');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      setSubmitting(true);
      
      // Register employee
      const registrationData = {
        inviteToken: formData.inviteToken,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || null,
      };

      const response = await inviteService.registerEmployee(registrationData);
      
      // Auto-login after successful registration
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify({
          userId: response.userId,
          businessId: response.businessId,
          businessName: response.businessName,
          email: response.email,
          firstName: response.firstName,
          lastName: response.lastName,
          subscriptionStatus: response.subscriptionStatus,
          hasValidAccess: response.hasValidAccess,
          role: 'Employee', // Set role
        }));
        
        // Redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Error registering:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Registration failed';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <Loader className="animate-spin text-primary-600 mx-auto mb-4" size={48} />
          <p className="text-gray-600 font-medium">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-red-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                This invitation link may have expired or been cancelled. 
                Please contact your business owner for a new invitation.
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn btn-primary w-full"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get business icon
  const businessIcon = businessTheme?.serviceType 
    ? industryIcons[businessTheme.serviceType] || industryIcons.Other
    : industryIcons.Other;

  // Get primary color for theme
  const primaryColor = businessTheme?.primaryColor || '#6366F1';

  // Registration form
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4" style={{
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
    }}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header with Business Branding */}
        <div className="text-white p-8 rounded-t-lg" style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}ee 100%)`
        }}>
          <div className="text-center">
            {/* Business Icon */}
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-5xl">{businessIcon}</span>
            </div>
            <h1 className="text-3xl font-bold mb-2">Join Your Team!</h1>
            <p className="text-white opacity-90">
              You've been invited to join <strong>{inviteData?.businessName}</strong>
            </p>
          </div>
        </div>

        {/* Invite Info */}
        <div className="border-b border-gray-200 p-6" style={{
          backgroundColor: `${primaryColor}10`
        }}>
          <div className="flex items-start gap-3 max-w-xl mx-auto">
            <Building2 className="flex-shrink-0 mt-1" size={20} style={{ color: primaryColor }} />
            <div className="flex-1">
              <p className="text-sm text-gray-700 mb-1">
                <strong>{inviteData?.invitedByName}</strong> has invited you to join their team
              </p>
              <p className="text-xs text-gray-600">
                Expires: {inviteData?.expiresAt ? new Date(inviteData.expiresAt).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="p-8">
          <div className="space-y-6 max-w-xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Create Your Account
              </h2>
              <p className="text-sm text-gray-600">
                Fill out the form below to complete your registration
              </p>
            </div>

            {/* Email (pre-filled, read-only) */}
            <div>
              <label className="label">
                <Mail size={16} className="inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                readOnly
                className="input bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                This email address was specified in your invitation
              </p>
            </div>

            {/* First Name */}
            <div>
              <label className="label">
                <User size={16} className="inline mr-1" />
                First Name *
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
                placeholder="John"
                maxLength="100"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="label">
                <User size={16} className="inline mr-1" />
                Last Name *
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
                placeholder="Doe"
                maxLength="100"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="label">
                <Phone size={16} className="inline mr-1" />
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input"
                placeholder="(555) 555-5555"
                maxLength="20"
              />
            </div>

            {/* Password */}
            <div>
              <label className="label">
                <Lock size={16} className="inline mr-1" />
                Password *
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input"
                placeholder="Minimum 8 characters"
                minLength="8"
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label className="label">
                <Lock size={16} className="inline mr-1" />
                Confirm Password *
              </label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="input"
                placeholder="Re-enter your password"
                minLength="8"
              />
            </div>

            {/* What you'll have access to */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 text-sm">
                As an employee, you'll have access to:
              </h3>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>✓ Dashboard and business overview</li>
                <li>✓ Customer management</li>
                <li>✓ Job tracking and scheduling</li>
                <li>✓ Lead management</li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 text-lg text-white font-semibold rounded-lg shadow-lg transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: primaryColor,
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader className="animate-spin" size={20} />
                  Creating Account...
                </span>
              ) : (
                'Complete Registration'
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              By registering, you agree to SuddyFlow's Terms of Service and Privacy Policy
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeRegistration;